import type {
  GuidedAnswer,
  GuidedAnswerAttributes,
  GuidedModeAttributeItem,
  GuidedModeCondition,
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeVisibilityRule,
} from '../types/guided-mode.types'
import { questionForStep } from './assessment-definition'

export type GuidedModeAnswers = Record<string, GuidedAnswer>
export type GuidedModeAnswerAttributes = Record<string, GuidedAnswerAttributes>

export function stepKey(step: GuidedModeStep): string {
  return step.id || step.questionId || (step.steps || [])
    .map(nestedStep => nestedStep.id || nestedStep.questionId)
    .join('|')
}

export function evaluateCondition(
  condition: GuidedModeCondition | GuidedModeVisibilityRule,
  answers: GuidedModeAnswers,
  today = new Date(),
): boolean {
  if ('all' in condition) return condition.all.every(child => evaluateCondition(child, answers, today))
  if ('any' in condition) return condition.any.some(child => evaluateCondition(child, answers, today))
  const answer = answers[condition.conceptId]
  const numericAnswer = typeof answer === 'number' || (typeof answer === 'string' && answer.trim() !== '')
    ? Number(answer)
    : Number.NaN
  const numericValue = Number(condition.value)
  const numbersAreComparable = Number.isFinite(numericAnswer) && Number.isFinite(numericValue)
  switch (condition.operator) {
    case 'days_since_gte': {
      if (typeof answer !== 'string' || !answer.trim()) return false
      const date = new Date(answer)
      const daysSince = Math.round((today.getTime() - date.getTime()) / 86400000)
      return Number.isFinite(daysSince) && Number.isFinite(numericValue) && daysSince >= numericValue
    }
    case 'eq':
      return answer === condition.value
    case 'ne':
      return answer !== condition.value
    case 'contains':
      return Array.isArray(answer) && answer.includes(String(condition.value))
    case 'not_contains':
      return !Array.isArray(answer) || !answer.includes(String(condition.value))
    case 'gt':
      return numbersAreComparable && numericAnswer > numericValue
    case 'gte':
      return numbersAreComparable && numericAnswer >= numericValue
    case 'lt':
      return numbersAreComparable && numericAnswer < numericValue
    case 'lte':
      return numbersAreComparable && numericAnswer <= numericValue
    default:
      return false
  }
}

export function attributeRows(
  step: GuidedModeStep,
  answers: GuidedModeAnswers,
): GuidedModeAttributeItem[] {
  return (step.items || []).filter(item => answers[item.conceptId] === 'yes')
}

export function isStepVisible(step: GuidedModeStep, answers: GuidedModeAnswers): boolean {
  if (step.kind === 'attribute') return attributeRows(step, answers).length > 0
  if (step.kind === 'group') {
    if (!(step.steps || []).some(nestedStep => isStepVisible(nestedStep, answers))) return false
  }
  if (!step.visibleWhen) return true
  return 'all' in step.visibleWhen
    ? step.visibleWhen.all.every(condition => evaluateCondition(condition, answers))
    : step.visibleWhen.any.some(condition => evaluateCondition(condition, answers))
}

// A group is considered "answered" once every nested question has a response,
// but the response shape depends on how the group is presented to the user:
//
//   * Multiselect groups render every nested question on screen at once and
//     typically only allow `yes`/`no` responses, so we still require that
//     exact answer shape to recognise completion.
//   * Sequential groups (non-multiselect) walk through each nested question
//     individually and accept any answer type — numeric values, blood
//     pressure strings, ISO dates, free text, etc. Treating those as "answered"
//     only when the value is literally `yes`/`no` makes the review-edit
//     `nextUnansweredIndex` flow force the user through every completed numeric
//     group again. Instead we accept any non-empty response, mirroring the
//     `allSequentialGroupAnswered` predicate in `useGuidedQuestionnaire`.
//
// Both branches are intentionally collection-aware: the predicate used by the
// review flow must agree with the predicate used during the normal flow,
// otherwise editing an earlier summary item re-opens already-completed steps.
function isGroupAnswered(
  step: GuidedModeStep,
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
): boolean {
  const questionSteps = (step.steps || [])
    .filter(nestedStep => isStepVisible(nestedStep, answers))
    .flatMap(nestedStep => {
      const question = questionForStep(definition, nestedStep)
      return question ? [{ conceptId: question.conceptId, step: nestedStep }] : []
    })
  if (questionSteps.length === 0) return false
  if (step.multiselect) {
    return questionSteps.every(({ conceptId }) => (
      answers[conceptId] === 'yes' || answers[conceptId] === 'no'
    ))
  }
  return questionSteps.every(({ conceptId, step: questionStep }) => {
    const answer = answers[conceptId]
    if (isUnpopulatedCalculatedDate(questionStep, answer)) return true
    if (Array.isArray(answer)) return answer.length > 0
    return answer !== undefined && answer !== null && answer !== ''
  })
}

function isUnpopulatedCalculatedDate(step: GuidedModeStep, answer: GuidedAnswer): boolean {
  return step.collection?.type === 'calculated_date'
    && (answer === undefined || answer === null || answer === '')
}

export function isStepAnswered(
  step: GuidedModeStep | null,
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  answerAttributes: GuidedModeAnswerAttributes,
): boolean {
  if (!step) return false
  if (step.kind === 'attribute') {
    const rows = attributeRows(step, answers)
    return rows.length > 0 && rows.every(row => (
      step.attribute !== undefined && Boolean(answerAttributes[row.conceptId]?.[step.attribute])
    ))
  }
  if (step.kind === 'group') return isGroupAnswered(step, definition, answers)
  const question = questionForStep(definition, step)
  const answer = question ? answers[question.conceptId] : undefined
  return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
}

export function hasOpenVisibleQuestion(
  step: GuidedModeStep,
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  answerAttributes: GuidedModeAnswerAttributes,
): boolean {
  const questionSteps = step.kind === 'group'
    ? (step.steps || []).filter(nestedStep => isStepVisible(nestedStep, answers))
    : [step]
  return questionSteps.some(questionStep => {
    const question = questionForStep(definition, questionStep)
    const answer = question ? answers[question.conceptId] : undefined
    if (step.kind === 'group'
      && !step.multiselect
      && isUnpopulatedCalculatedDate(questionStep, answer)) return false
    return !isStepAnswered(questionStep, definition, answers, answerAttributes)
  })
}

export function findFirstOpenStepIndex(
  steps: GuidedModeStep[],
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  answerAttributes: GuidedModeAnswerAttributes,
): number {
  return steps.findIndex(step => (
    hasOpenVisibleQuestion(step, definition, answers, answerAttributes)
  ))
}

export function findNextUnansweredStepIndex(
  steps: GuidedModeStep[],
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  answerAttributes: GuidedModeAnswerAttributes,
  afterIndex = -1,
): number {
  return steps.findIndex((step, index) => (
    index > afterIndex && !isStepAnswered(step, definition, answers, answerAttributes)
  ))
}
